import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Clock, Calendar, Loader2 } from 'lucide-react';
import { Blog } from '../../types/blog';
import { blogService } from '../../services/blogService';
import { BlogCard } from './BlogCard';
import { Button } from '../common/Button';
// import { mockBlogs } from '../../constants/mockBlogs';
import { trackSectionView, trackButtonClick } from '../../firebaseConfig';
import { useAnalyticsTracking } from '../../hooks/useAnalyticsTracking';

export function BlogSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [featuredBlogs, setFeaturedBlogs] = useState<Blog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { trackButtonClick: trackAnalyticsButton, trackSectionView: trackAnalyticsSection } = useAnalyticsTracking();

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            // Track section view
            trackSectionView('Blog Section');
            trackAnalyticsSection('Blog Section', {
              section_name: 'blog_section',
              section_position: 'after_testimonials',
              blog_count: featuredBlogs.length
            });
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -100px 0px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [trackAnalyticsSection, featuredBlogs.length]);

  // Fetch featured blogs
  useEffect(() => {
    const fetchFeaturedBlogs = async () => {
      try {
        setIsLoading(true);
        const blogs = await blogService.getFeaturedBlogs(3);
        console.log('featuredBlogs', blogs);
        setFeaturedBlogs(blogs);
        trackAnalyticsSection('Blog Section', {
          section_name: 'blog_section',
          section_position: 'after_testimonials',
          blog_count: featuredBlogs.length
        });
      } catch (error) {
        console.error('Error fetching featured blogs:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeaturedBlogs();
  }, []);

  if (!isLoading && featuredBlogs.length === 0) {
    return null; // Don't render section if no blogs
  }

  if(featuredBlogs.length === 0){
    return null;
  }

  return (
    <section
      ref={sectionRef}
      className="py-24 relative overflow-hidden"
    >

      {/* Floating particles */}
      <div 
        className="absolute left-[20%] top-[15%] w-5 h-5 rounded-full bg-primary-light/15 dark:bg-primary-dark/15 blur-md animate-float opacity-0 transition-opacity duration-700 delay-250" 
        style={{ opacity: isVisible ? 0.6 : 0, animationDelay: '0.5s', animationDuration: '6s' }}
      />
      <div 
        className="absolute right-[25%] top-[30%] w-4 h-4 rounded-full bg-secondary-light/15 dark:bg-secondary-dark/15 blur-md animate-float opacity-0 transition-opacity duration-700 delay-300" 
        style={{ opacity: isVisible ? 0.7 : 0, animationDelay: '0.3s', animationDuration: '5s' }}
      />

      <div className="container mx-auto max-w-7xl px-4 mb-12 md:px-0 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          
          <h2 
            className={`text-3xl md:text-4xl font-bold mb-6 text-text-primary-light dark:text-text-primary-dark transform transition-all duration-700 delay-150 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            Read More - Learn More With MockTrail
          </h2>
          
          <p 
            className={`text-lg text-text-secondary-light dark:text-text-secondary-dark max-w-3xl mx-auto transform transition-all duration-700 delay-300 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            Discover expert advice, industry insights, and proven strategies to excel in your interviews and advance your career.
          </p>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-12 h-12 text-primary-light dark:text-primary-dark animate-spin" />
          </div>
        )}

        {/* Featured Blogs Grid */}
        {!isLoading && featuredBlogs.length > 0 && (
          <div 
            className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12 transform transition-all duration-700 delay-500 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            } auto-rows-fr`}
          >
            {featuredBlogs.slice(0, 6).map((blog, index) => (
              <div
                key={blog._id}
                className="transform transition-all duration-700 hover:scale-[1.02] h-full"
                style={{ 
                  transitionDelay: `${600 + index * 100}ms`,
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(32px)'
                }}
              >
                <BlogCard 
                  blog={blog} 
                  variant="default"
                  showAuthor={false}
                  showViewCount={false}
                  showTags={true}
                />
              </div>
            ))}
          </div>
        )}

        {/* Call to Action */}
        {!isLoading && featuredBlogs.length > 0 && (
          <div 
            className={`flex justify-center items-center transform transition-all duration-700 delay-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <Link 
              to="/blogs"
              onClick={() => {
                trackButtonClick('View All Articles', 'Blog Section');
                trackAnalyticsButton('View All Articles', 'Blog Section', {
                  button_type: 'secondary_cta',
                  button_position: 'blog_section_footer',
                  conversion_intent: 'blog_discovery',
                  utm_source: 'blog_section',
                  utm_medium: 'cta_button',
                  utm_campaign: 'view_all_blogs'
                });
              }}
            >
              {featuredBlogs.length >= 3 && ( 
              <Button 
                variant="secondary" 
                size="md"
                rightIcon={<ArrowRight className="w-5 h-5" />}
                analyticsName="View All Blogs"
                analyticsLocation="Blog Section"
                analyticsMetadata={{ section: 'homepage_blog_section' }}
              >
                Read More Articles
              </Button>
              )}
            </Link>
          </div>
        )}

      </div>
    </section>
  );
} 